import React, { useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  ManageAccounts,
  Save,
  Refresh,
  Security,
  Lock
} from '@mui/icons-material';

const UserSettings = () => {
  const [settings, setSettings] = useState({
    // Registration Settings
    allowRegistration: true,
    requireEmailVerification: true,
    requirePhoneVerification: false,
    autoApproveUsers: false,
    
    // Password Policy
    minPasswordLength: '8',
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    passwordExpiry: '90',
    
    // Session Settings
    sessionTimeout: '30',
    maxConcurrentSessions: '3',
    rememberMeDuration: '30',
    
    // Two-Factor Authentication
    enable2FA: false,
    require2FAForAdmin: true,
    require2FAForAgent: false,
    
    // Account Security
    maxLoginAttempts: '5',
    lockoutDuration: '30',
    enableAccountLockout: true,
    
    // User Permissions
    defaultFarmerPermissions: 'standard',
    defaultAgentPermissions: 'standard',
    allowProfileEdit: true,
    allowPasswordChange: true
  });

  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (event) => {
    setSettings({
      ...settings,
      [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
    });
  };

  const handleSave = () => {
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  const passwordStrength = () => {
    let strength = 0;
    if (parseInt(settings.minPasswordLength) >= 8) strength++;
    if (settings.requireUppercase) strength++;
    if (settings.requireLowercase) strength++;
    if (settings.requireNumbers) strength++;
    if (settings.requireSpecialChars) strength++;
    
    if (strength <= 2) return { label: 'Weak', color: 'error' };
    if (strength <= 3) return { label: 'Medium', color: 'warning' };
    return { label: 'Strong', color: 'success' };
  };

  const rolePermissions = [
    { role: 'Farmer', create: true, read: true, update: true, delete: false, export: false },
    { role: 'Agent', create: true, read: true, update: true, delete: false, export: true },
    { role: 'Admin', create: true, read: true, update: true, delete: true, export: true },
    { role: 'AIC Officer', create: true, read: true, update: true, delete: true, export: true }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          User & Security Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure user registration, authentication, and security policies
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      {/* Registration Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Registration Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.allowRegistration}
                    onChange={handleChange('allowRegistration')}
                  />
                }
                label="Allow User Registration"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoApproveUsers}
                    onChange={handleChange('autoApproveUsers')}
                    disabled={!settings.allowRegistration}
                  />
                }
                label="Auto-Approve New Users"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.requireEmailVerification}
                    onChange={handleChange('requireEmailVerification')}
                    disabled={!settings.allowRegistration}
                  />
                }
                label="Require Email Verification"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.requirePhoneVerification}
                    onChange={handleChange('requirePhoneVerification')}
                    disabled={!settings.allowRegistration}
                  />
                }
                label="Require Phone Verification"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Password Policy */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lock /> Password Policy
            </Typography>
            <Chip 
              label={`Strength: ${passwordStrength().label}`} 
              color={passwordStrength().color}
              size="small"
            />
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Password Length"
                value={settings.minPasswordLength}
                onChange={handleChange('minPasswordLength')}
                type="number"
                inputProps={{ min: 6, max: 32 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password Expiry (days)"
                value={settings.passwordExpiry}
                onChange={handleChange('passwordExpiry')}
                type="number"
                helperText="0 = Never expires"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.requireUppercase}
                    onChange={handleChange('requireUppercase')}
                  />
                }
                label="Require Uppercase Letters"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.requireLowercase}
                    onChange={handleChange('requireLowercase')}
                  />
                }
                label="Require Lowercase Letters"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.requireNumbers}
                    onChange={handleChange('requireNumbers')}
                  />
                }
                label="Require Numbers"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.requireSpecialChars}
                    onChange={handleChange('requireSpecialChars')}
                  />
                }
                label="Require Special Characters"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Session Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Session Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Session Timeout (minutes)"
                value={settings.sessionTimeout}
                onChange={handleChange('sessionTimeout')}
                type="number"
                helperText="Idle time before logout"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Max Concurrent Sessions"
                value={settings.maxConcurrentSessions}
                onChange={handleChange('maxConcurrentSessions')}
                type="number"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Remember Me Duration (days)"
                value={settings.rememberMeDuration}
                onChange={handleChange('rememberMeDuration')}
                type="number"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security /> Two-Factor Authentication
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enable2FA}
                    onChange={handleChange('enable2FA')}
                  />
                }
                label="Enable Two-Factor Authentication"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.require2FAForAdmin}
                    onChange={handleChange('require2FAForAdmin')}
                    disabled={!settings.enable2FA}
                  />
                }
                label="Require 2FA for Admins"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.require2FAForAgent}
                    onChange={handleChange('require2FAForAgent')}
                    disabled={!settings.enable2FA}
                  />
                }
                label="Require 2FA for Agents"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Account Security */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Account Security
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.enableAccountLockout}
                    onChange={handleChange('enableAccountLockout')}
                  />
                }
                label="Enable Account Lockout"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Login Attempts"
                value={settings.maxLoginAttempts}
                onChange={handleChange('maxLoginAttempts')}
                type="number"
                disabled={!settings.enableAccountLockout}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Lockout Duration (minutes)"
                value={settings.lockoutDuration}
                onChange={handleChange('lockoutDuration')}
                type="number"
                disabled={!settings.enableAccountLockout}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* User Permissions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            User Permissions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.allowProfileEdit}
                    onChange={handleChange('allowProfileEdit')}
                  />
                }
                label="Allow Users to Edit Profile"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.allowPasswordChange}
                    onChange={handleChange('allowPasswordChange')}
                  />
                }
                label="Allow Users to Change Password"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Role Permissions Matrix */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Role Permissions Matrix
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell align="center"><strong>Create</strong></TableCell>
                  <TableCell align="center"><strong>Read</strong></TableCell>
                  <TableCell align="center"><strong>Update</strong></TableCell>
                  <TableCell align="center"><strong>Delete</strong></TableCell>
                  <TableCell align="center"><strong>Export</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rolePermissions.map((perm) => (
                  <TableRow key={perm.role}>
                    <TableCell><strong>{perm.role}</strong></TableCell>
                    <TableCell align="center">
                      <Chip label={perm.create ? '✓' : '✗'} color={perm.create ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={perm.read ? '✓' : '✗'} color={perm.read ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={perm.update ? '✓' : '✗'} color={perm.update ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={perm.delete ? '✓' : '✗'} color={perm.delete ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={perm.export ? '✓' : '✗'} color={perm.export ? 'success' : 'default'} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => window.location.reload()}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
        >
          Save Settings
        </Button>
      </Box>
    </Box>
  );
};

export default UserSettings;
